import { Button } from 'primereact/button';
import React from 'react';
import AuthService from 'services/AuthService';
import FileService from 'services/FileService';
import { useRegenerateEpdDocument } from 'services/api/mutations';
import { useEPDDefinitionInformation, useEpdPDFDocument } from 'services/api/queries';
import styled from 'styled-components';
import { FieldPanelFullWidth, FieldPanelHeader } from 'styles/v2/Styles.styled';

import TextWithAnimatedEllipsis from '../file-upload/TextWithAnimatedEllipsis';
import { HelpBox } from '../help-boxes';
import { ButtonUploadIcon } from '../icons';
import { toaster } from '../toast';
import QRCodeDownloadDialog from './dialogs/QRCodeDownloadDialog';
import { usePcrV2Loading } from './hooks/usePcrV2';
import SkeletonStep6 from './skeletons/SkeletonStep6';

const Step6: React.FunctionComponent<{
  epdId: string;
  epdVersionId: string | undefined;
}> = ({ epdId, epdVersionId }) => {
  const epdDefinition = useEPDDefinitionInformation(epdVersionId!).data;
  const pdfDocument = useEpdPDFDocument(epdVersionId!).data;
  const regenerateEpdDocumentMutation = useRegenerateEpdDocument(epdId, epdVersionId!);
  const isPcrV2Loading = usePcrV2Loading();
  const isLoading = epdDefinition === undefined || pdfDocument === undefined || isPcrV2Loading;
  if (isLoading) {
    return <SkeletonStep6 />;
  }

  const compilerDocumentId = pdfDocument?.id;

  const handleDownloadPDF = () => {
    window.open(FileService.getImageUrl(compilerDocumentId ?? ''), '_blank', 'noreferrer');
  };

  const handleRegeneratePDF = async () => {
    try {
      await regenerateEpdDocumentMutation.mutateAsync();
      toaster({
        severity: 'success',
        summary: 'Success',
        details: 'The EPD document has been created successfully.',
      });
    } catch (error: any) {
      toaster({
        severity: 'error',
        summary: 'Error',
        details: error?.response?.data?.detail || 'The document has not been created.',
      });
    }
  };

  const isEpdDocumentAbsent = !!epdDefinition?.publishedVersionId && !compilerDocumentId;
  const canRegenerateEpdDocument = isEpdDocumentAbsent && (AuthService.isAdmin() || AuthService.isSystemAdmin());
  const isNoDocumentTextVisible =
    isEpdDocumentAbsent && !canRegenerateEpdDocument && epdDefinition.publishedVersionId === epdVersionId;
  const shouldShowRegenerateSection =
    isEpdDocumentAbsent && canRegenerateEpdDocument && epdDefinition.publishedVersionId === epdVersionId;

  return (
    <>
      <FieldPanelFullWidth>
        <FieldPanelHeader>Generated EPD</FieldPanelHeader>
        <div>
          {shouldShowRegenerateSection && (
            <div style={{ marginBottom: 16 }}>
              {regenerateEpdDocumentMutation.isPending ? (
                <TextWithAnimatedEllipsis className="mt-2">
                  Generation of the document is in progress
                </TextWithAnimatedEllipsis>
              ) : (
                <RegeneratePDFWarningBox>
                  <span>The EPD document has not been created.</span>
                  <Button
                    style={{ fontSize: 14 }}
                    type="button"
                    label="Regenerate PDF"
                    onClick={handleRegeneratePDF}
                    disabled={false}
                  />
                </RegeneratePDFWarningBox>
              )}
            </div>
          )}

          <HelpBox>
            After the review and publication are completed by the Secretariat, you will be able to download the generated EPD
            document in PDF format from this section.
          </HelpBox>
          {epdDefinition?.publishedVersionId && compilerDocumentId && (
            <DownloadPDFDocumentBox>
              <Button
                icon={ButtonUploadIcon}
                style={{ fontSize: 14, fontWeight: 500 }}
                type="button"
                label="Download PDF"
                text
                onClick={handleDownloadPDF}
                disabled={false}
              />
            </DownloadPDFDocumentBox>
          )}
        </div>

        {isNoDocumentTextVisible && (
          <>Compiler generated document had not been created or deleted. Please contact administrator.</>
        )}
      </FieldPanelFullWidth>
      <FieldPanelFullWidth>
        <FieldPanelHeader>Generate Traceability Logotype</FieldPanelHeader>
        <div>
          <HelpBox>
            After publication, you can generate a traceability logotype with a link to the published EPD on environdec.com
          </HelpBox>
          <QRCodeDownloadDialog
            epdId={epdId}
            friendlyUrl={epdDefinition?.friendlyUrl}
            registrationNumber={epdDefinition?.fullIdentificationNumber}
            disabled={!epdDefinition?.publishedVersionId}
          />
        </div>
      </FieldPanelFullWidth>
    </>
  );
};

const DownloadPDFDocumentBox = styled.div`
  width: 100%;
  border-radius: 4px;
  border: none;
  display: flex;
  justify-content: start;
  align-items: center;
  max-height: 70px;
  margin-top: 10px;
  cursor: pointer;

  .p-button.p-button-text {
    padding: 0;
    background-color: transparent;
    border: none;
    ${(props) => props.theme.fonts.standard};
  }
  .p-button.p-button-text:hover {
    background-color: transparent;
  }
  .p-button.p-button-text:disabled {
    color: #757575;
  }
`;

const RegeneratePDFWarningBox = styled.div`
  width: 100%;
  border-radius: 4px;
  border: none;
  background: ${(props) => props.theme.colors.primaryOrange01};
  color: ${(props) => props.theme.colors.systemStatusError};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;

  .p-button {
    background: ${(props) => props.theme.colors.primaryOrange};
    border-color: ${(props) => props.theme.colors.primaryOrange};
    color: ${(props) => props.theme.colors.primaryWhite};
    padding: 2px 6px;
  }

  .p-button:hover {
    background: ${(props) => props.theme.colors.elementsButtonHover};
    border-color: ${(props) => props.theme.colors.elementsButtonHover};
  }

  span {
    ${(props) => props.theme.typography.contentInput};
  }
`;

export default Step6;
